import {
  Action,
  Character as ScreenplayCharacter,
  Dialogue,
  ElementType,
  PageBreak,
  Parenthetical,
  SceneHeading,
  Script,
  TitleEntry,
  Transition,
  type Element,
} from "screenplay-tools/javascript/src/screenplay.js";
import { FDXParser } from "screenplay-tools/javascript/src/fdx/parser.js";
import { FDXWriter } from "screenplay-tools/javascript/src/fdx/writer.js";
import { FountainParser } from "screenplay-tools/javascript/src/fountain/parser.js";
import { FountainWriter } from "screenplay-tools/javascript/src/fountain/writer.js";
import { parseScript, rebuildSections } from "./parser";
import type { Character, ImportMode, ParseResult, ParserMode, ScriptDocument, ScriptEntry, ScriptPage } from "./types";

export type ScreenplayFileFormat = "fountain" | "fdx";

function newId(): string {
  if (typeof globalThis.crypto?.randomUUID === "function") return globalThis.crypto.randomUUID();
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
}

function elementText(element: Element): string {
  return String(element.text ?? "").trim();
}

function characterKey(name: string): string {
  return name.replace(/\s+/g, " ").trim().toUpperCase();
}

function titleFromScript(script: Script, fallback: string): string {
  const title = script.titleEntries.find((entry) => entry.key.trim().toLowerCase() === "title");
  return elementText(title ?? { text: "" } as Element) || fallback.replace(/\.(fountain|fdx)$/i, "").trim() || "Untitled Script";
}

/** Parse a screenplay format into screenplay-tools' format-neutral Script model. */
export function parseScreenplayFormat(source: string, format: ScreenplayFileFormat): Script {
  if (format === "fdx") {
    // screenplay-tools configures FDX paragraph text as the XML parser's text
    // node. Flatten FDX's optional <Text> runs so this also works with current
    // fast-xml-parser releases, while retaining the library's FDX mapping.
    const compatibleSource = source.replace(/<Text(?:\s[^>]*)?>/gi, "").replace(/<\/Text>/gi, "");
    return new FDXParser().parse(compatibleSource);
  }
  const parser = new FountainParser();
  parser.useTags = true;
  parser.addText(source);
  return parser.script;
}

export function parseSource(
  sourceText: string,
  parserMode: ParserMode = "auto",
  importMode: ImportMode = "text",
  suggestedTitle = "Untitled Script",
): ParseResult {
  if (parserMode !== "fountain" && parserMode !== "fdx") return parseScript(sourceText, parserMode, importMode, suggestedTitle);
  return documentFromScreenplay(parseScreenplayFormat(sourceText, parserMode), sourceText, parserMode, importMode, suggestedTitle);
}

/** Project the library's intermediate model into LineByLine's rehearsal document. */
export function documentFromScreenplay(
  screenplay: Script,
  sourceText: string,
  parserMode: Extract<ParserMode, ScreenplayFileFormat>,
  importMode: ImportMode,
  suggestedTitle: string,
): ParseResult {
  const document = parseScript("", "none", importMode, titleFromScript(screenplay, suggestedTitle)).document;
  const entries: ScriptEntry[] = [];
  const characters = new Map<string, Character>();
  let activeCharacter: Character | undefined;
  let parenthetical = "";
  let pageNumber = 1;

  const characterFor = (name: string): Character => {
    const key = characterKey(name);
    let character = characters.get(key);
    if (!character) {
      character = { id: newId(), name: name.trim(), aliases: [key], colour: `hsl(${(characters.size * 67 + 250) % 360} 65% 62%)` };
      characters.set(key, character);
    }
    return character;
  };
  const add = (kind: ScriptEntry["kind"], text: string, extra: Partial<ScriptEntry> = {}): void => {
    if (!text && kind !== "pageMarker") return;
    entries.push({ id: newId(), kind, text, rawText: text, pageNumber, ...extra });
  };

  for (const element of screenplay.elements) {
    const text = elementText(element);
    if (element.type === ElementType.CHARACTER) {
      const cue = element as ScreenplayCharacter;
      activeCharacter = characterFor(cue.name);
      parenthetical = cue.extension ? `(${cue.extension})` : "";
      continue;
    }
    if (element.type === ElementType.PARENTHETICAL) {
      parenthetical = `(${text})`;
      continue;
    }
    if (element.type === ElementType.DIALOGUE) {
      const dialogue = [parenthetical, text].filter(Boolean).join(" ");
      add("dialogue", dialogue, activeCharacter ? { speakerId: activeCharacter.id, speakerLabel: activeCharacter.name } : {});
      parenthetical = "";
      continue;
    }

    activeCharacter = undefined;
    parenthetical = "";
    if (element.type === ElementType.HEADING || element.type === ElementType.SECTION) add("heading", text);
    else if (element.type === ElementType.PAGEBREAK) {
      pageNumber += 1;
      add("pageMarker", `Page ${pageNumber}`);
    } else if (element.type === ElementType.NOTE || element.type === ElementType.SYNOPSIS) add("note", text);
    else if (element.type === ElementType.ACTION || element.type === ElementType.TRANSITION || element.type === ElementType.LYRIC) add("stageDirection", text);
  }

  const pages = new Map<number, ScriptPage>();
  for (const item of entries) {
    const number = item.pageNumber ?? 1;
    let page = pages.get(number);
    if (!page) {
      page = { id: newId(), pageNumber: number, entries: [] };
      pages.set(number, page);
    }
    page.entries.push(item);
  }
  document.title = titleFromScript(screenplay, suggestedTitle);
  document.sourceText = sourceText;
  document.parserMode = parserMode;
  document.pages = [...pages.values()];
  if (!document.pages.length) document.pages = [{ id: newId(), pageNumber: 1, entries: [] }];
  document.characters = [...characters.values()];
  document.tags = [...new Set(screenplay.elements.flatMap((element) => element.tags ?? []))];
  rebuildSections(document);
  return { document, issues: [] };
}

/** Convert the editable rehearsal document back to the library's intermediate model. */
export function screenplayFromDocument(document: ScriptDocument): Script {
  const screenplay = new Script();
  screenplay.titleEntries.push(new TitleEntry("Title", document.title));
  for (const page of document.pages) {
    for (const item of page.entries) {
      if (item.kind === "dialogue") {
        const speaker = item.speakerLabel ?? document.characters.find((character) => character.id === item.speakerId)?.name ?? "Reader";
        screenplay.addElement(new ScreenplayCharacter(speaker));
        const match = item.text.match(/^\(([^)]+)\)\s*(.*)$/s);
        if (match) {
          screenplay.addElement(new Parenthetical(match[1]!));
          screenplay.addElement(new Dialogue(match[2]!));
        } else screenplay.addElement(new Dialogue(item.text));
      } else {
        if (item.kind === "heading") screenplay.addElement(new SceneHeading(item.text));
        else if (item.kind === "pageMarker") screenplay.addElement(new PageBreak());
        else if (item.kind === "note") screenplay.addElement(new Action(item.text));
        else if (/^[A-Z][A-Z ]+TO:$/.test(item.text)) screenplay.addElement(new Transition(item.text));
        else screenplay.addElement(new Action(item.text));
      }
    }
  }
  return screenplay;
}

export function exportFountain(document: ScriptDocument): string {
  return new FountainWriter().write(screenplayFromDocument(document));
}

export function exportFdx(document: ScriptDocument): string {
  return new FDXWriter().write(screenplayFromDocument(document));
}
